import { useMemo } from 'react'

import { useParams, usePathname } from 'next/navigation'

import { isTransactionPage } from '~/components/Layout'
import { isPublicInvoicePage } from '~/components/Layout/isPublicInvoicePage'
import { useAuth } from '~/context/AuthProvider'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useIsMobile } from '~/utils/devices'

/**
 * Hide sidebar when:
 * - On a transaction path, unless a dialog pushed that path over another page
 * - On component library page
 * - User is not authenticated
 * - On mobile
 */
export function useHideSidebar() {
  const pathname = usePathname()
  const { user: authUser } = useAuthUser()
  const { globalModalState } = useAuth()
  const isMobile = useIsMobile()
  const {
    onboarding: { isOnboarding },
  } = useAuth()

  const params = useParams()
  const isOnTransactionPage = isTransactionPage(pathname)
  const isOnPublicInvoicePage = isPublicInvoicePage(pathname, params)

  const isPublicPage = pathname === '/' || isOnTransactionPage

  const hasModalContent =
    globalModalState.isOpen || globalModalState.content !== null
  // A transaction path in the address bar is either the standalone transaction
  // page or a dialog opened over another page (useDialogRoutePath pushes the
  // path without changing the rendered route). Only the latter keeps the page,
  // and its sidebar, underneath the dialog. The route params tell them apart:
  // the pushed path leaves the page's own params in place, so there is no
  // transaction id.
  const isDialogPushedPath = hasModalContent && !params.transaction

  const isSplitsConnectPage = pathname.startsWith('/connect')

  const hideSidebar = useMemo(() => {
    // These pages should always have the sidebar hidden
    if (
      pathname.startsWith('/compliance/accept-invitation') ||
      pathname.startsWith('/docs') ||
      pathname.startsWith('/auth') ||
      pathname.startsWith('/invalid-link')
    ) {
      return true
    }

    if (isMobile) return true
    if (isPublicPage && isOnboarding) return true
    if (isOnTransactionPage && !isDialogPushedPath) return true
    if (isOnPublicInvoicePage) return true
    if (!authUser) return true

    if (isSplitsConnectPage) return true

    return false
  }, [
    isOnboarding,
    isPublicPage,
    pathname,
    isOnTransactionPage,
    isOnPublicInvoicePage,
    authUser,
    isMobile,
    isDialogPushedPath,
    isSplitsConnectPage,
  ])

  return hideSidebar
}
